import React, { Component } from 'react';
import '../../../../../styles/components/our-partner.scss';
import { getClientsNew } from '../../../../../models/organization';
import i18n from '../../../../../i18n';

class PartnersSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
      partners: [],
      displayedPartners: [],
      loading: false,
    };
    this.sectionRef = React.createRef();
    this.animationIntervals = [];
    this.rotationIndices = {}; // Track index for each rotation group separately
  }

  componentDidMount() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState({ isVisible: true });

            // Load partners only once
            if (this.state.partners.length === 0) {
              this.loadPartners();
            }

            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (this.sectionRef.current) {
      this.observer.observe(this.sectionRef.current);
    }
  }

  componentWillUnmount() {
    if (this.observer && this.sectionRef.current) {
      this.observer.unobserve(this.sectionRef.current);
    }
    // Clear all intervals
    this.animationIntervals.forEach((interval) => clearInterval(interval));
  }

  async loadPartners() {
    try {
      this.setState({ loading: true });

      const response = await getClientsNew();

      const partners = (response?.data || []).map((item) => ({
        id: item.id,
        name: item.organization_name,
        name_ar: item.organization_name_ar,
        logo: item.organization_image_url,
      }));

      this.setState({
        partners,
        displayedPartners: partners.slice(0, 8),
        loading: false,
      });

      // Start rotation only if more than 8
      if (partners.length > 8) {
        this.startRotation();
      }
    } catch (error) {
      console.error('Failed to load partners', error);
      this.setState({ loading: false });
    }
  }

  startRotation = () => {
    const { partners } = this.state;

    if (partners.length <= 8) return;

    // Positions to rotate with separate tracking
    const rotationConfig = [
      { positions: [0, 1], direction: 'up', interval: 4000, key: 'topLeft' },
      { positions: [2, 3], direction: 'left', interval: 4500, key: 'topRight' },
      { positions: [4, 5], direction: 'right', interval: 5000, key: 'bottomLeft' },
      { positions: [6, 7], direction: 'down', interval: 5500, key: 'bottomRight' },
    ];

    // Initialize separate index for each rotation group
    rotationConfig.forEach((config) => {
      this.rotationIndices[config.key] = 8; // Start from 9th partner
    });

    rotationConfig.forEach((config) => {
      const interval = setInterval(() => {
        if (this.state.partners.length <= 8) {
          clearInterval(interval);
          return;
        }

        // Get current index for this specific rotation group
        let currentIndex = this.rotationIndices[config.key];

        this.setState((prevState) => {
          const newDisplayed = [...prevState.displayedPartners];

          config.positions.forEach((position) => {
            // Get next partner in rotation
            const nextPartner = prevState.partners[currentIndex % prevState.partners.length];

            // Add sliding class
            const element = document.querySelector(`[data-position="${position}"]`);
            if (element) {
              element.classList.add(`slide-out-${config.direction}`);

              // Replace after animation
              setTimeout(() => {
                newDisplayed[position] = nextPartner;
                this.setState({ displayedPartners: newDisplayed });

                // Remove sliding class and add slide-in
                setTimeout(() => {
                  const newElement = document.querySelector(`[data-position="${position}"]`);
                  if (newElement) {
                    newElement.classList.remove(`slide-out-${config.direction}`);
                    newElement.classList.add(`slide-in-${config.direction}`);

                    setTimeout(() => {
                      newElement.classList.remove(`slide-in-${config.direction}`);
                    }, 600);
                  }
                }, 50);
              }, 600);
            }

            currentIndex++;
          });

          // Update the index for this rotation group
          this.rotationIndices[config.key] = currentIndex;

          return { displayedPartners: newDisplayed };
        });
      }, config.interval);

      this.animationIntervals.push(interval);
    });
  };

  render() {
    const { isVisible, displayedPartners, loading } = this.state;
    const isArabic = i18n.language.startsWith('ar');

    return (
      <section
        ref={this.sectionRef}
        className={`partners-section ${isVisible ? 'animate-in' : ''}`}
      >
        <div className="partners-container">
          {/* Section Header */}
          <div className="partners-header">
            <div className="partners-subtitle">{i18n.t('trusted_by')}</div>
            <h2 className="partners-title">{i18n.t('our_partners')}</h2>
            <div className="partners-divider"></div>
            <p className="partners-description">{i18n.t('partner_description')}</p>
          </div>

          {/* Loading State - Skeleton */}
          {loading && (
            <div className="partners-grid">
              {[...Array(8)].map((_, index) => (
                <div key={`skeleton-${index}`}>
                  <div className="partner-card-inner skeleton-card-inner">
                    <div className="skeleton-logo"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Partners Grid */}
          {!loading && displayedPartners.length > 0 && (
            <div className="partners-grid">
              {displayedPartners.map((partner, index) => (
                <div
                  key={`${partner.id}-${index}`}
                  data-position={index}
                  className="partner-card"
                  style={{ animationDelay: `${index * 0.3}s` }}
                >
                  <div className="partner-card-inner">
                    <img src={partner.logo} alt={partner.name} className="partner-logo" />
                    <div className="partner-overlay">
                      <span className="partner-name">
                        {isArabic ? partner.name_ar : partner.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && displayedPartners.length === 0 && (
            <div className="partners-empty">
              <p>No partners to display</p>
            </div>
          )}

          {/* Animated Background Elements */}
          <div className="partners-bg-decoration">
            <div className="decoration-circle decoration-circle-1"></div>
            <div className="decoration-circle decoration-circle-2"></div>
            <div className="decoration-circle decoration-circle-3"></div>
          </div>
        </div>
      </section>
    );
  }
}

export default PartnersSection;
